import React, { useCallback, useMemo, useState } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { Button } from '../../shared/button/Button';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../shared/tooltip';
import { fetchAllUsersStarted, fetchAvailableFeaturePermissionsStarted } from '../users/store';
import styles from './Roles.module.scss';
import { duplicateRole, fetchAllRolesStarted, getAllRoles, getDeleteConfirmationDialogOpen, getFetchingRoles, Role, toggleDeleteRoleConfirmationModal, toggleRoleModal } from './store';
import { RoleDialog } from './RoleDialog';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { FeaturePermissionDisplay } from './FeaturePermissionDisplay';
import { fetchAllAccessGroupsStarted, getAllAccessGroups } from '../access-groups/store';
import { IconButton } from '../../shared/button/IconButton';
import { ActionModal } from '../../shared/modal/ActionModal';
import { Duplicate, Bin, Functions } from '../../shared/icons';
import { PlusButton } from '../../shared/button/PlusButton';
import { Position } from '../../shared/modal/PositionModal';

const NAV_BAR = 100;
const PADDING = 20;
const HEADER = 50;
const INFORMATION = 42;
const OFFSET = NAV_BAR + PADDING + HEADER + INFORMATION;

export const Roles: React.FC = () => {
    useFetchStarted([fetchAllRolesStarted(), fetchAvailableFeaturePermissionsStarted(), fetchAllAccessGroupsStarted(), fetchAllUsersStarted()]);

    const dispatch = useAppDispatch();
    const [, screenHeight] = useWindowResize();
    const [actionModalOpen, setActionModalOpen] = useState<Role | null>(null);
    const [position, setPosition] = useState<Position | null>(null);

    const openModal = useCallback((x: number, y: number, role: Role) => {
        setPosition({ x, y });
        setActionModalOpen(role);
    }, []);

    const closeUserActionModal = useCallback(() => {
        setPosition(null);
        setActionModalOpen(null);
    }, []);

    const fetchingRoles = useAppSelector(getFetchingRoles);
    const allRoles = useAppSelector(getAllRoles);
    const allAccessGroups = useAppSelector(getAllAccessGroups);
    const deleteConfirmationModalOpen = useAppSelector(getDeleteConfirmationDialogOpen);

    const scrollableHeight = useMemo(() => screenHeight - OFFSET, [screenHeight]);

    const toggleModal = useCallback((role?: Role) => dispatch(toggleRoleModal(true, role)), [dispatch]);

    const duplicateSelectedRole = useCallback((role: Role) => dispatch(duplicateRole(role)), [dispatch]);

    const deleteRole = useCallback((role: Role) => dispatch(toggleDeleteRoleConfirmationModal(true, role)), [dispatch]);
    const closeDeleteConfirmationModal = useCallback(() => dispatch(toggleDeleteRoleConfirmationModal(false)), [dispatch]);

    const getAccessGroupsForRoleId = useCallback((accessGroupIds: number[]) => allAccessGroups
        .filter(ag => accessGroupIds.includes(ag.accessGroupId!))
        .map(({ accessGroupName }) => accessGroupName)
    , [allAccessGroups]);

    const roleActions = useCallback((role: Role) => [
        { label: 'Duplicate', icon: Duplicate, onClick: () => duplicateSelectedRole(role), withSeparator: true },
        { label: 'Delete', icon: Bin, onClick: () => deleteRole(role), isDelete: true }
    ], [deleteRole, duplicateSelectedRole]);

    return (
        <>
            <div className={styles.rolesWrapper}>
                <div className={styles.titleContainer}>
                    <div className={styles.title}>Roles</div>
                    <PlusButton onClick={() => toggleModal()} fontSize={22} testId='create-role' />
                </div>
                <div className={styles.disclaimer}>
                    Roles are an easy way to assign particular feature permissions to a group or team of users in the application.
                    {allRoles.length === 0 ? 'You do not currently have any roles configured.' : ''}
                </div>
                <Scrollable height={`${scrollableHeight}px`}>
                    <div className={styles.tilesColumnWrapper}>
                        {fetchingRoles ? <div className={styles.loadingWrapper}><LoadingDots /></div> : allRoles.map((role, index) => {
                            const { roleName, roleDescription, featurePermissions, accessGroupIds, userIds } = role;
                            const accessGroupNames = getAccessGroupsForRoleId(accessGroupIds!);
                            const usersAssigned = `${userIds.length} : Users Assigned`;
                            return (
                                <div key={index} className={styles.roleTileWrapper} >
                                    <div className={styles.roleInfoWrapper}>
                                        <div className={styles.roleNameWrapper}>
                                            <div className={styles.roleName}>
                                                <OverflowTooltip overlayText={roleName} />
                                            </div>
                                            <div className={styles.usersAssigned}>{usersAssigned}</div>
                                        </div>
                                        <div className={styles.roleDescription}>
                                            <Scrollable>
                                                <>{roleDescription}</>
                                            </Scrollable>
                                        </div>
                                        <div className={styles.label}>
                                            Access Groups
                                        </div>
                                        <Scrollable height='40px'>
                                            <div className={styles.roleAccessGroups}>
                                                {accessGroupNames.length > 0 ? accessGroupNames.map((accessGroupName, index) => (
                                                    <div key={index} className={styles.roleAccessGroup}>
                                                        <OverflowTooltip overlayText={accessGroupName} />
                                                    </div>
                                                )) : <i>There are no access groups assigned to this role</i>}
                                            </div>
                                        </Scrollable>
                                        <div className={styles.label}>
                                            Permitted Features
                                        </div>
                                        <FeaturePermissionDisplay featurePermissions={featurePermissions} />
                                    </div>
                                    <div className={styles.buttonWrapper}>
                                        <IconButton icon={Functions} onClick={e => openModal(e.clientX, e.clientY, role)} />
                                        {actionModalOpen &&
                                            <ActionModal
                                                isOpen={!isNull(actionModalOpen)}
                                                position={position}
                                                actions={roleActions(actionModalOpen)}
                                                closeModal={closeUserActionModal}
                                                testId='role-buttons-modal'
                                            />}
                                        <Button label='Edit' onClick={() => toggleModal(role)} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Scrollable>
            </div>
            <RoleDialog />
            <DeleteConfirmationModal isOpen={deleteConfirmationModalOpen} closeModal={closeDeleteConfirmationModal} />
        </>
    );
};
